import React, { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { API_URL } from '../config';
import CourseCard from '../components/CourseCard';
import FilterBar from '../components/FilterBar';
import useCoursesSSE from '../hooks/useCoursesSSE';
import './CoursesPage.css';

const initialState = {
  courses: [],
  page: 1,
  hasMore: true,
  loading: false,
  error: null,
  filters: { search: '', tags: [], sort: 'recent' }
};

const coursesReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING': 
      return { ...state, loading: action.payload, error: action.payload ? null : state.error };
    case 'SET_ERROR': 
      return { ...state, error: action.payload, loading: false };
    case 'SET_FILTERS': 
      return { ...state, filters: action.payload, courses: [], page: 1, hasMore: true, error: null };
    case 'INCREMENT_PAGE': 
      return { ...state, page: state.page + 1 };
    case 'ADD_COURSE':
      if (state.courses.some(c => String(c._id) === String(action.payload._id))) return state;
      return { ...state, courses: [action.payload, ...state.courses] };
    case 'UPDATE_COURSE':
      return {
        ...state,
        courses: state.courses.map(c =>
          String(c._id) === String(action.payload._id) ? action.payload : c
        )
      };
    case 'DELETE_COURSE':
      return {
        ...state,
        courses: state.courses.filter(c => String(c._id) !== String(action.payload._id))
      };
    case 'APPEND_COURSES':
      if (action.payload.page === 1) {
        const totalPages = action.payload.totalPages ?? 1;
        return { ...state, courses: action.payload.courses, loading: false, hasMore: action.payload.page < totalPages, error: null };
      } else {
        const newCourses = action.payload.courses.filter(c => !state.courses.some(existing => String(existing._id) === String(c._id)));
        const totalPages = action.payload.totalPages ?? 1;
        return { ...state, courses: [...state.courses, ...newCourses], loading: false, hasMore: action.payload.page < totalPages, error: null };
      }
    default: return state;
  }
};

const sortMap = { recent: 'recientes', price_asc: 'precio_asc', price_desc: 'precio_desc' };
const COURSES_PAGE_LIMIT = 100;

const CoursesPage = () => {
  const [state, dispatch] = useReducer(coursesReducer, initialState);
  const [isSticky, setIsSticky] = useState(false);
  const observer = useRef();
  const filtersRef = useRef(state.filters);
  const activeFetchRef = useRef(null);
  const filtersStickyRef = useRef(null);

  // 1. DEFINICIÓN DE CALLBACKS (Antes de ser usados en useEffect)
  
  const fetchCourses = useCallback(async (page, filters) => {
    // Abortar petición previa para evitar race conditions
    if (activeFetchRef.current) activeFetchRef.current.abort();
    const controller = new AbortController();
    activeFetchRef.current = controller;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const query = new URLSearchParams({
        page,
        limit: COURSES_PAGE_LIMIT,
        search: filters.search,
        tags: (filters.tags || []).join(','),
        sort: sortMap[filters.sort] || 'recientes'
      });
      
      const res = await fetch(`${API_URL}/api/cursos-publicos?${query}`, { 
        headers: { 'Accept': 'application/json' },
        signal: controller.signal 
      });
      
      if (!res.ok) throw new Error('Error al cargar cursos');
      const data = await res.json();
      
      dispatch({ 
        type: 'APPEND_COURSES', 
        payload: { 
          courses: Array.isArray(data) ? data : (data.items || []), 
          page, 
          totalPages: typeof data.totalPages === 'number' ? data.totalPages : 1 
        } 
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const isNarrow =
        typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 1024px)').matches;
      if (isNarrow && filtersStickyRef.current) {
        requestAnimationFrame(() => {
          filtersStickyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, []);

  const lastCourseElementRef = useCallback(node => {
    if (state.loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && state.hasMore) dispatch({ type: 'INCREMENT_PAGE' });
    });
    if (node) observer.current.observe(node);
  }, [state.loading, state.hasMore]);

  // 2. EFECTOS (Invocaciones y sincronización)

  // Sincronizar ref de filtros para evitar cierres obsoletos (stale closures)
  useEffect(() => { 
    filtersRef.current = state.filters; 
  }, [state.filters]);

  // Manejo de scroll para barra pegajosa
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 220);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch inicial y por cambio de página/filtros
  useEffect(() => {
    fetchCourses(state.page, state.filters);
  }, [state.page, state.filters, fetchCourses]);

  // Limpieza al desmontar
  useEffect(() => {
    return () => activeFetchRef.current?.abort();
  }, []);

  // Polling solo para la página 1
  useEffect(() => {
    if (state.page !== 1) return;
    const intervalId = setInterval(() => fetchCourses(1, filtersRef.current), 60000);
    return () => clearInterval(intervalId);
  }, [fetchCourses, state.page]);

  // Integración SSE para tiempo real
  useCoursesSSE(dispatch, useCallback(async () => {
    try {
      const query = new URLSearchParams({
        page: 1, limit: COURSES_PAGE_LIMIT, search: state.filters.search,
        tags: (state.filters.tags || []).join(','), sort: sortMap[state.filters.sort] || 'recientes'
      });
      const res = await fetch(`${API_URL}/api/cursos-publicos?${query}`, { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        const courses = Array.isArray(data) ? data : (data.items || []);
        courses.forEach(c => {
          const exists = state.courses.some(existing => String(existing._id) === String(c._id));
          dispatch({ type: exists ? 'UPDATE_COURSE' : 'ADD_COURSE', payload: c });
        });
      }
    } catch (error) {
      console.error("SSE refetch failed:", error);
    }
  }, [state.filters, state.courses]));

  return (
    <div className="courses-page">
      <div className="courses-hero">
        <div className="courses-hero-content container">
          <h1>Catálogo de Cursos</h1>
          <p>Descubre nuestra selección de cursos y lleva tus habilidades al siguiente nivel.</p>
        </div>
      </div>

      <div className="courses-main-content container">
        <div className="filters-sticky-container" ref={filtersStickyRef}>
          <div className={`filters-wrapper ${isSticky ? 'is-sticky' : ''}`}>
            <FilterBar onFilterChange={handleFilterChange} isCompact={isSticky} />
          </div>
        </div>
        
        {state.error && (
          <div className="state-message error-message">
            <span>{state.error}</span>
          </div>
        )}
        
        <div className="courses-grid">
          {state.courses.map((course, index) => (
            <div 
              ref={state.courses.length === index + 1 ? lastCourseElementRef : null} 
              key={course._id} 
              className="course-card-wrapper"
            >
              <CourseCard course={course} />
            </div>
          ))}
        </div>
        
        {state.loading && (
          <div className="state-message loading-message">
            <div className="spinner"></div><span>Cargando cursos...</span>
          </div>
        )}

        {!state.hasMore && state.courses.length > 0 && (
          <div className="state-message end-message">
            <span>Has visto todos los cursos disponibles</span>
          </div>
        )}

        {!state.loading && state.courses.length === 0 && !state.error && (
          <div className="empty-state">
            <h3>No se encontraron cursos</h3>
            <p>Intenta ajustar los filtros de búsqueda o cambiar las categorías.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;